import React, { useState, useEffect } from 'react';
import { Typography, Button, Space, Row, Col, Card, Rate, Tag, Image } from 'antd';
import { RightOutlined, StarOutlined, ClockCircleOutlined } from '@ant-design/icons';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getImageForCard } from '../../utils/images';

const { Title, Text } = Typography;

const FeaturedSection = ({ 
  title = "Featured Items",
  subtitle = "Discover our most popular dishes",
  items = [],
  type = "menu", // "menu" or "vendor"
  showViewAll = true,
  onViewAll,
  loading = false,
  cardStyle = {},
  sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  }
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const renderMenuItem = (item) => (
    <Card
      hoverable
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
        height: '100%',
        ...cardStyle
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ height: '200px', overflow: 'hidden' }}>
        <Image
          src={getImageForCard(item)}
          alt={item.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          preview={false}
          fallback="/images/placeholder.svg"
        />
      </div>
      <div style={{ padding: '16px' }}>
        <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
          {item.name}
        </Title>
        {item.rating && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
            <Rate 
              disabled 
              value={item.rating} 
              style={{ fontSize: '12px' }}
            />
            <Text type="secondary" style={{ fontSize: '11px', marginLeft: '4px' }}>
              ({item.total_reviews || 0})
            </Text>
          </div>
        )}
        <div style={{ marginTop: '8px' }}>
          {item.preparation_time && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              <ClockCircleOutlined /> {item.preparation_time} min
            </Text>
          )}
        </div>
        {item.is_vegetarian && (
          <Tag color="green" style={{ fontSize: '10px', marginTop: '4px' }}>
            Veg
          </Tag>
        )}
      </div>
    </Card>
  );

  const renderVendorItem = (item) => (
    <Card
      hoverable
      style={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #f0f0f0',
        height: '100%',
        ...cardStyle
      }}
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ height: '180px', overflow: 'hidden', position: 'relative' }}>
        <Image
          src={getImageForCard(item)}
          alt={item.business_name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
          preview={false}
          fallback="/images/placeholder.svg"
        />
        {item.is_verified && (
          <div style={{ 
            position: 'absolute', 
            top: '8px', 
            right: '8px',
            background: '#52c41a',
            color: 'white',
            padding: '2px 6px',
            borderRadius: '4px',
            fontSize: '10px',
            fontWeight: 'bold'
          }}>
            ✓
          </div>
        )}
      </div>
      <div style={{ padding: '16px' }}>
        <Title level={5} style={{ margin: 0, fontSize: '16px' }}>
          {item.business_name}
        </Title>
        {item.rating && (
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '4px' }}>
            <Rate 
              disabled 
              value={item.rating} 
              style={{ fontSize: '12px' }}
            />
            <Text type="secondary" style={{ fontSize: '11px', marginLeft: '4px' }}>
              ({item.total_reviews || 0})
            </Text>
          </div>
        )}
        <div style={{ marginTop: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {item.cuisines && item.cuisines.slice(0, 2).join(', ')}
          </Text>
        </div>
        {item.preparation_time && (
          <Text type="secondary" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
            <ClockCircleOutlined /> {item.preparation_time} min delivery
          </Text>
        )}
      </div>
    </Card>
  );

  const renderItem = (item) => {
    return type === 'menu' ? renderMenuItem(item) : renderVendorItem(item);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <div style={{ marginBottom: '48px' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '24px'
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            {title}
          </Title>
          {subtitle && (
            <Text type="secondary" style={{ fontSize: '16px' }}>
              {subtitle}
            </Text>
          )}
        </div>
        {showViewAll && (
          <Button 
            type="primary" 
            icon={<RightOutlined />}
            onClick={onViewAll}
            style={{ borderRadius: '6px' }}
          >
            View All
          </Button>
        )}
      </div>

      {/* Slider */}
      <div 
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Slider {...sliderSettings}>
          {items.map((item, index) => (
            <div key={item.id || index} style={{ padding: '0 8px' }}>
              {renderItem(item)}
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

export default FeaturedSection;
